import { useTranslation } from 'react-i18next';

import {
  Button,
  Flex,
  Icon,
  Item,
  ItemContent,
  ItemTitle,
  Text,
  Badge,
  cn,
  Skeleton,
} from '@kognitos/lattice';
import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';
import { type IGroupedConnectionsByIntegration } from '@/modules/integrations/utils';
import AutomationConnectionMenu from './AutomationConnectionMenu';
import { useRetrieveWorkspaceBook } from '@/generated/hooks/bdk/v2/services/library';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { convertToDataUrl } from '@/shared/utils/image-utils';
import { getInitials } from '@/shared/utils/integrations-utils';

interface IAutomationIntegrationItemProps {
  integrationWithConnections: IGroupedConnectionsByIntegration;
  automationId: string;
  isDraft?: boolean;
  readonly?: boolean;
  /** Latest available version of this integration, provided by the parent panel. */
  latestVersion?: string;
  /** Whether an upgrade is in progress (panel-level flag). */
  isUpgrading?: boolean;
  /**
   * Called when the user clicks Upgrade.
   * Receives the current and latest book descriptors so the panel can detect
   * OAuth scope changes and trigger re-authentication after the upgrade runs.
   */
  onUpgrade?: (
    currentBook?: Bdkv2BookDescriptor,
    latestBook?: Bdkv2BookDescriptor,
  ) => void;
}

function AutomationIntegrationItem({
  integrationWithConnections,
  automationId,
  isDraft,
  readonly = false,
  latestVersion,
  isUpgrading = false,
  onUpgrade,
}: IAutomationIntegrationItemProps) {
  const { t: tAutomation } = useTranslation('automation');

  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const {
    data: integrationData,
    isLoading: isLoadingIntegration,
    isError: isErrorIntegration,
  } = useRetrieveWorkspaceBook(
    organizationId,
    workspaceId,
    integrationWithConnections.book,
    integrationWithConnections.book_version,
    {
      query: {
        enabled:
          !!organizationId &&
          !!workspaceId &&
          !!integrationWithConnections.book &&
          !!integrationWithConnections.book_version,
      },
    },
  );

  const integration = integrationData?.data.book;

  const imageSrc = convertToDataUrl(integration?.icon);

  // Fetch the latest book descriptor so the panel can detect OAuth scope changes
  // and trigger re-authentication for the affected connections after upgrading.
  const {
    data: latestBookData,
    isError: isErrorLatestBook,
    isLoading: isLoadingLatestBook,
  } = useRetrieveWorkspaceBook(
    organizationId,
    workspaceId,
    integrationWithConnections.book,
    latestVersion ?? '',
    {
      query: {
        enabled: Boolean(integrationWithConnections.book && latestVersion),
      },
    },
  );

  const latestBook = latestBookData?.data.book;

  const isUpgradeAvailable = Boolean(
    latestVersion &&
      latestVersion !== integrationWithConnections.book_version &&
      !isErrorLatestBook &&
      !isLoadingLatestBook,
  );

  const handleUpgrade = () => {
    onUpgrade?.(integration, latestBook);
  };

  const renderIntegrationHeader = () => {
    if (isLoadingIntegration) {
      return (
        <Flex
          gap={2}
          align="center"
          data-testid="automation-integration-loading-skeleton"
        >
          <Skeleton className="size-6 bg-alternate-1" />
          <Skeleton className="w-30 h-6 bg-alternate-1" />
          <Skeleton className="w-10 h-4 bg-alternate-1" />
        </Flex>
      );
    }

    return (
      <Flex gap={2} align="center">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={integration?.display_name}
            className="size-6 object-contain"
          />
        ) : (
          <div className="size-6 bg-muted flex items-center justify-center rounded text-sm font-medium text-muted-foreground">
            {getInitials(integration?.display_name || '')}
          </div>
        )}
        <Text level="large">
          {integration?.display_name || integrationWithConnections.book}
        </Text>
        <Badge variant="outline" className="text-xs bg-background">
          {integrationWithConnections.book_version}
        </Badge>
        {isErrorIntegration && (
          <Badge variant="outline" className="bg-accent">
            <Flex gap={1} align="center">
              <Icon type="AlertCircle" size="xs" className="text-warning" />
              {tAutomation(
                'automation.integrations.integrationDetailsUnavailable',
              )}
            </Flex>
          </Badge>
        )}
      </Flex>
    );
  };

  return (
    <Flex
      direction="column"
      gap={3}
      className="bg-secondary dark:bg-card p-3 rounded-md"
      width="full"
    >
      <Flex justify="between" align="center" gap={2} width="full">
        {renderIntegrationHeader()}
        {!readonly &&
          !isLoadingIntegration &&
          !isErrorIntegration &&
          isUpgradeAvailable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleUpgrade}
              isLoading={isUpgrading}
              disabled={isUpgrading}
            >
              {tAutomation('automation.integrations.upgrade')}
            </Button>
          )}
      </Flex>
      {integrationWithConnections.connections.map(
        ({
          connection_alias,
          production_credentials_available,
          test_credentials_available,
          test_connection_id,
          connection_name,
          connection_id,
        }) => {
          const isTrigger = !connection_alias;
          const connectionExists = Boolean(
            isTrigger
              ? test_connection_id
              : connection_alias && test_connection_id,
          );
          return (
            <Item
              className="bg-popover dark:bg-secondary border-border w-full p-4 pr-2"
              key={connection_alias || connection_id}
            >
              <ItemContent>
                <Flex gap={2} justify="between">
                  <Flex direction="column" gap={2}>
                    <ItemTitle
                      className={cn('text-sm font-normal', {
                        'text-muted-foreground': !connectionExists,
                      })}
                    >
                      {test_credentials_available
                        ? connection_name
                        : 'Not connected'}
                    </ItemTitle>
                    <Flex gap={1} align="center">
                      <Icon
                        className="text-muted-foreground"
                        type={isTrigger ? 'Zap' : 'Diamond'}
                        size="xs"
                      />
                      <Text level="small" color="muted">
                        {isTrigger
                          ? tAutomation('automation.integrations.trigger')
                          : tAutomation('automation.integrations.action')}
                      </Text>
                    </Flex>
                  </Flex>
                  {/* NOTE: we always need to pass the test connection id
                    As any modification to connection happens via draft, which always uses test connection.
                    For triggers (no alias), show badge only without menu */}
                  <AutomationConnectionMenu
                    connectionAlias={connection_alias}
                    testConnectionId={test_connection_id}
                    isProdAvailable={production_credentials_available}
                    isTestAvailable={test_credentials_available}
                    integration={integrationData?.data.book}
                    isIntegrationUnavailable={isErrorIntegration}
                    automationId={automationId}
                    isDraft={isDraft}
                    readonly={readonly}
                    isTrigger={isTrigger}
                  />
                </Flex>
              </ItemContent>
            </Item>
          );
        },
      )}
    </Flex>
  );
}

export default AutomationIntegrationItem;
